import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type {
  ComplianceArtifactCreate,
  ComplianceArtifactUploadUrlRequest,
  ComplianceChecklistActIn,
  ComplianceChecklistAttachIn,
  ComplianceEarlierCycleCreate,
  ComplianceFiledCopyAttach,
  ComplianceFilingAttachmentCreate,
  ComplianceFilingCaseOpen,
  ComplianceFilingGroupCreate,
  ComplianceFilingGroupMemberMove,
  ComplianceFilingGroupMove,
  ComplianceFilingGroupRename,
  ComplianceFilingRecordCreate,
  ComplianceFormAnswerIn,
  ComplianceFormVersionMintIn,
  ComplianceObligationRegistrationDateRecord,
  ComplianceOrganizationFilingCreate,
  ComplianceOrganizationFilingEarlierCycle,
  ComplianceOrganizationFilingFilingRecord,
  ComplianceOrganizationFilingSubmissionRecord,
  ComplianceOrganizationFilingUpdate,
  ComplianceProfileParameterRecord,
  ComplianceProfileReviewFlagResolve,
  ComplianceProfileSuggestionResolve,
  ComplianceRegistrationImportAmendmentCreate,
  ComplianceRegistrationImportCreate,
  ComplianceRenewalFilingRecordCreate,
  ComplianceRenewalSubmissionRecord,
  ComplianceSigningActEchoes,
  ComplianceSigningDecline,
  ComplianceSigningRequestCancellation,
  ComplianceSigningRequestExpiry,
  ComplianceSigningRequestIssue,
  ComplianceSigningRequestRevision,
  ComplianceSigningSettingWrite,
  ComplianceSigningSignature,
  ComplianceSubmissionAuthorize,
  ComplianceSubmissionDispatch,
  ComplianceSubmissionEvidenceAnswer,
  ComplianceSubmissionEvidenceRecord,
  ComplianceSubmissionHandoffRecord,
  ComplianceSubmissionReconciliation,
  ComplianceSubmissionRecord,
  ComplianceVendorRecordWrite,
  ComplianceWindowClosedRecord,
} from '@givmo/api-client';
import type { ComplianceAiSettingWrite } from './compliance-ai-setting';

/**
 * CCM-R1-ADD-SHEET-WHICH-YEAR: the year a filing or a send was for, where its date has no on-time
 * reading. A typed extension ahead of the api-client's regen (`agent_docs/api-client.md`, additive
 * request fields): the HTTP bodies take `filed_for`, and the sheet sends it only where the picker's
 * read offered years, which only a backend that takes it serves.
 */
export interface ComplianceFilingYearBody {
  cycle_key: string;
  deadline_index: number;
}
export type ComplianceSubmissionBody = ComplianceSubmissionRecord & {
  filed_for?: ComplianceFilingYearBody;
};
export type ComplianceRegistrationImportBody = ComplianceRegistrationImportCreate & {
  filed_for?: ComplianceFilingYearBody;
};
import type { ExecutedCopyBody } from './compliance-signed-outside-model';
import { guardComplianceOperations } from './compliance-standing';

export interface CompliancePage {
  limit?: number;
  offset?: number;
}

export interface ProfileParameterPage extends CompliancePage {
  includeSuperseded?: boolean;
}

/**
 * The complete host-operation boundary for the module.
 *
 * Every method but the last two maps one-for-one to a server operation. The byte upload and the
 * disclosure resolver are also injected because neither is module-owned transport. The package
 * never constructs a host URL or reaches for an app-configured client.
 */
export interface ComplianceOperations {
  listObligations: (orgRef: string, page?: CompliancePage) => Promise<unknown>;
  getObligation: (orgRef: string, obligationId: string) => Promise<unknown>;
  /**
   * CCM-OPS1-REPIN (G4): what changed between the rule version a filing is pinned to and its rule's
   * current one (`ComplianceObligationRuleChangeOut`).
   */
  getObligationRuleChange: (orgRef: string, obligationId: string) => Promise<unknown>;
  /**
   * CCM-OPS1-REPIN: move a filing on the calendar to its rule's current version, the one the
   * comparison showed, by the organization's own act. A version that is no longer current is
   * refused, and nothing moves.
   */
  repinObligation: (
    orgRef: string,
    obligationId: string,
    body: { rule_version_id: string },
  ) => Promise<unknown>;
  /**
   * CCM-R1-TRACKING-PANEL (D-CCM-58): stop tracking a filing on the calendar from the filing
   * itself, and start it again. Each answers the filing; a repeat answers it as it stands, and a
   * filing not on the calendar is refused (T1, `COMPLIANCE_TRACKING_NOT_ON_CALENDAR`).
   */
  stopTracking: (orgRef: string, obligationId: string) => Promise<unknown>;
  resumeTracking: (orgRef: string, obligationId: string) => Promise<unknown>;
  listOccurrences: (orgRef: string, page?: CompliancePage) => Promise<unknown>;
  recordSubmission: (
    orgRef: string,
    obligationId: string,
    body: ComplianceSubmissionBody,
  ) => Promise<unknown>;
  recordWindowClosed: (
    orgRef: string,
    obligationId: string,
    body: ComplianceWindowClosedRecord,
  ) => Promise<unknown>;
  recordRegistrationDate: (
    orgRef: string,
    obligationId: string,
    body: ComplianceObligationRegistrationDateRecord,
  ) => Promise<unknown>;
  recordFiling: (
    orgRef: string,
    obligationId: string,
    body: ComplianceFilingRecordCreate,
  ) => Promise<unknown>;
  issueArtifactUploadUrl: (
    orgRef: string,
    body: ComplianceArtifactUploadUrlRequest,
  ) => Promise<unknown>;
  confirmArtifact: (orgRef: string, body: ComplianceArtifactCreate) => Promise<unknown>;
  listArtifacts: (orgRef: string, page?: CompliancePage) => Promise<unknown>;
  listObligationArtifacts: (orgRef: string, obligationId: string) => Promise<unknown>;
  getArtifactDownload: (orgRef: string, artifactId: string) => Promise<unknown>;
  deleteArtifact: (orgRef: string, artifactId: string) => Promise<void>;
  getProfile: (orgRef: string) => Promise<unknown>;
  listProfileParameters: (orgRef: string, page?: ProfileParameterPage) => Promise<unknown>;
  recordProfileParameter: (
    orgRef: string,
    body: ComplianceProfileParameterRecord,
  ) => Promise<unknown>;
  listProfileSuggestions: (orgRef: string, page?: CompliancePage) => Promise<unknown>;
  resolveProfileSuggestion: (
    orgRef: string,
    suggestionId: string,
    body: ComplianceProfileSuggestionResolve,
  ) => Promise<unknown>;
  listProfileReviewFlags: (orgRef: string, page?: CompliancePage) => Promise<unknown>;
  resolveProfileReviewFlag: (
    orgRef: string,
    reviewFlagId: string,
    body: ComplianceProfileReviewFlagResolve,
  ) => Promise<unknown>;
  listRegistrationImports: (orgRef: string, page?: CompliancePage) => Promise<unknown>;
  importRegistration: (orgRef: string, body: ComplianceRegistrationImportBody) => Promise<unknown>;
  amendRegistrationImport: (
    orgRef: string,
    importId: string,
    body: ComplianceRegistrationImportAmendmentCreate,
  ) => Promise<unknown>;
  listRegistrationImportAmendments: (orgRef: string, importId: string) => Promise<unknown>;
  // ── The organization's registered agents and foreign qualifications (CCM-MOD5B) ─────────────
  /**
   * Its vendor records, current and ended, each with the confirmed library filings of its kind in
   * its jurisdiction (`library_filing_ids`, D-CCM-94 (4)).
   */
  listVendorRecords: (orgRef: string, page?: CompliancePage) => Promise<unknown>;
  /** One record, and each write's snapshot with who made it and when. */
  getVendorRecord: (orgRef: string, vendorId: string) => Promise<unknown>;
  /**
   * Record, correct, end or make current again: the whole set of facts, against the revision read
   * (0 makes a record with the id the caller chose).
   */
  recordVendor: (
    orgRef: string,
    vendorId: string,
    body: ComplianceVendorRecordWrite,
  ) => Promise<unknown>;
  /**
   * CCM-MOD5-RECORD-FILING-LINK (D-CCM-97): say a foreign qualification record is that library
   * filing, and undo it. Each names the pair: a repeat answers the link as it stands, and an unlink
   * can never undo someone else's later link.
   */
  linkVendorRecord: (orgRef: string, vendorId: string, obligationId: string) => Promise<unknown>;
  unlinkVendorRecord: (orgRef: string, vendorId: string, obligationId: string) => Promise<unknown>;
  /** "Use AI on your organization’s data": the organization's setting (every Compliance reader). */
  getAiSetting: (orgRef: string) => Promise<unknown>;
  /** Turn Givmo's AI on or off, from the revision the change was made from (the module's Owner). */
  setAiSetting: (orgRef: string, body: ComplianceAiSettingWrite) => Promise<unknown>;
  // ── The Filings page's records (CCM-R1-INTAKE (a); D-CCM-21) ────────────────────────────────
  /** The add sheet's picker: library filings, one jurisdiction's or all (CCM-R1-FILING-META). */
  listFilingOptions: (
    orgRef: string,
    page?: CompliancePage,
    jurisdictionCode?: string,
    /** CCM-R1-ADD-SHEET-NEXT-FROM-ENTRY: the date of the last filing the sheet holds, ISO. */
    lastFilingOn?: string,
    /** The year named for it where its date places on none ("Which year was this filing for?"). */
    filedFor?: ComplianceFilingYearBody,
  ) => Promise<unknown>;
  /** The organization's filing groups, in its order (CCM-R1-FILING-GROUPS). */
  listFilingGroups: (orgRef: string, page?: CompliancePage) => Promise<unknown>;
  createFilingGroup: (orgRef: string, body: ComplianceFilingGroupCreate) => Promise<unknown>;
  renameFilingGroup: (
    orgRef: string,
    groupId: string,
    body: ComplianceFilingGroupRename,
  ) => Promise<unknown>;
  moveFilingGroup: (
    orgRef: string,
    groupId: string,
    body: ComplianceFilingGroupMove,
  ) => Promise<unknown>;
  removeFilingGroup: (orgRef: string, groupId: string) => Promise<void>;
  moveFilingToGroup: (
    orgRef: string,
    groupId: string,
    body: ComplianceFilingGroupMemberMove,
  ) => Promise<unknown>;
  /** The filings the organization records itself (CCM-R1-OWN-FILINGS). */
  listOrganizationFilings: (orgRef: string, page?: CompliancePage) => Promise<unknown>;
  getOrganizationFiling: (orgRef: string, filingId: string) => Promise<unknown>;
  addOrganizationFiling: (
    orgRef: string,
    body: ComplianceOrganizationFilingCreate,
  ) => Promise<unknown>;
  updateOrganizationFiling: (
    orgRef: string,
    filingId: string,
    body: ComplianceOrganizationFilingUpdate,
  ) => Promise<unknown>;
  recordOrganizationFilingFiled: (
    orgRef: string,
    filingId: string,
    cycleId: string,
    body: ComplianceOrganizationFilingFilingRecord,
  ) => Promise<unknown>;
  recordOrganizationFilingSent: (
    orgRef: string,
    filingId: string,
    cycleId: string,
    body: ComplianceOrganizationFilingSubmissionRecord,
  ) => Promise<unknown>;
  addOrganizationFilingCycle: (
    orgRef: string,
    filingId: string,
    body: ComplianceOrganizationFilingEarlierCycle,
  ) => Promise<unknown>;
  /** A library filing's years (CCM-R1-FILING-CYCLES): every cycle, or one obligation's. */
  listRenewalCycles: (
    orgRef: string,
    page?: CompliancePage,
    obligationId?: string,
  ) => Promise<unknown>;
  /** Close one year with the agency's confirmation, and the filing as sent if given. */
  recordRenewalFiling: (
    orgRef: string,
    cycleId: string,
    body: ComplianceRenewalFilingRecordCreate,
  ) => Promise<unknown>;
  /** Record that one named year was sent. Sent is never filed. */
  recordRenewalSubmission: (
    orgRef: string,
    cycleId: string,
    body: ComplianceRenewalSubmissionRecord,
  ) => Promise<unknown>;
  attachRenewalFiledCopy: (
    orgRef: string,
    cycleId: string,
    body: ComplianceFiledCopyAttach,
  ) => Promise<unknown>;
  addRenewalAttachment: (
    orgRef: string,
    cycleId: string,
    body: ComplianceFilingAttachmentCreate,
  ) => Promise<unknown>;
  /** A one-time filing keeps its documents on its obligation. */
  attachObligationFiledCopy: (
    orgRef: string,
    obligationId: string,
    body: ComplianceFiledCopyAttach,
  ) => Promise<unknown>;
  addObligationAttachment: (
    orgRef: string,
    obligationId: string,
    body: ComplianceFilingAttachmentCreate,
  ) => Promise<unknown>;
  /** "Add an earlier year": a cycle for a year before the latest one held (year-keyed shapes only). */
  addEarlierRenewalCycle: (
    orgRef: string,
    obligationId: string,
    body: ComplianceEarlierCycleCreate,
  ) => Promise<unknown>;
  // ── Preparing a filing's form (CCM-R1-DOCS; CCM-R1-DOCS-READS) ──────────────────────────────
  /** The forms Givmo prepares, each with the rule it is filed under. */
  listFormAdapters: (orgRef: string, page?: CompliancePage) => Promise<unknown>;
  /** A filing's cases (`obligation_id`), oldest first: the open one holds its form. */
  listFilingCases: (
    orgRef: string,
    obligationId: string,
    page?: CompliancePage,
  ) => Promise<unknown>;
  /** Open a case for one filing: what kind it is and, for a year, that year's record. */
  openFilingCase: (
    orgRef: string,
    obligationId: string,
    body: ComplianceFilingCaseOpen,
  ) => Promise<unknown>;
  /** A case's form preparations, oldest first — the READ way to a case's form. */
  listCasePreparations: (orgRef: string, caseId: string, page?: CompliancePage) => Promise<unknown>;
  /** Start preparing the case's form (a second start answers the first). */
  startFormPreparation: (orgRef: string, caseId: string) => Promise<unknown>;
  getFormPreparation: (orgRef: string, preparationId: string) => Promise<unknown>;
  answerFormItem: (
    orgRef: string,
    preparationId: string,
    itemKey: string,
    body: ComplianceFormAnswerIn,
  ) => Promise<unknown>;
  /**
   * One page of the form as it stands, drawn by the server (a PNG). Its `signal` aborts the request
   * when the page goes (CCM-R1-WORKBENCH-PREVIEW-SUPERSEDED).
   */
  getFormPagePreview: (
    orgRef: string,
    preparationId: string,
    page: number,
    options?: { signal?: AbortSignal },
  ) => Promise<Blob>;
  /** Record the form as it stands, echoing the `values_sha256` that was read (Download). */
  recordFormVersion: (
    orgRef: string,
    preparationId: string,
    body: ComplianceFormVersionMintIn,
  ) => Promise<unknown>;
  /** A short-lived link to one version's exact bytes (the download is logged). */
  getDocumentVersionDownload: (
    orgRef: string,
    documentId: string,
    versionId: string,
  ) => Promise<unknown>;
  getFilingCaseChecklist: (orgRef: string, caseId: string) => Promise<unknown>;
  attachChecklistDocument: (
    orgRef: string,
    caseId: string,
    attachmentIndex: number,
    body: ComplianceChecklistAttachIn,
  ) => Promise<unknown>;
  recordChecklistAct: (
    orgRef: string,
    caseId: string,
    attachmentIndex: number,
    body: ComplianceChecklistActIn,
  ) => Promise<unknown>;
  /** A case's signing requests, read only here: whether the form is out for signature. */
  listCaseSigningRequests: (
    orgRef: string,
    caseId: string,
    page?: CompliancePage,
  ) => Promise<unknown>;
  /**
   * One request with its live version: the signer's view (their places, what they can do now) and
   * the sender's (every place's standing) in one read (CCM-R1-SIGN; the panel's Signed line, the
   * signer page).
   */
  getSigningRequest: (orgRef: string, requestId: string) => Promise<unknown>;
  // ── Signing a request (CCM-R1-SIGN-WEB, the signer's side) ──────────────────────────────────
  /** One filing case: the filing it is for and its year, for a request's title (signer page). */
  getFilingCase: (orgRef: string, caseId: string) => Promise<unknown>;
  /** One filing document with every version, oldest first: where a signed form's final is found. */
  getFilingDocument: (orgRef: string, documentId: string) => Promise<unknown>;
  /** One version's drawn pages (a signed final's "as signed" pages; 404 for a final composed before B5). */
  getDocumentVersionRendition: (
    orgRef: string,
    documentId: string,
    versionId: string,
  ) => Promise<unknown>;
  /** Sign every place the caller holds on the live version, echoing exactly what they were shown. */
  signSigningRequest: (
    orgRef: string,
    requestId: string,
    body: ComplianceSigningSignature,
  ) => Promise<unknown>;
  /** Decline the caller's places, with an optional reason for the sender. Not final: they can still sign. */
  declineSigningRequest: (
    orgRef: string,
    requestId: string,
    body: ComplianceSigningDecline,
  ) => Promise<unknown>;
  /** Withdraw the caller's signature while other places are still open. */
  withdrawSignature: (
    orgRef: string,
    requestId: string,
    body: ComplianceSigningActEchoes,
  ) => Promise<unknown>;
  /** The organization's sign-in check before signing (every Compliance reader). */
  getSigningSetting: (orgRef: string) => Promise<unknown>;
  /** Change it, from the revision it was read at (a Compliance Owner or Global Admin). */
  setSigningSetting: (orgRef: string, body: ComplianceSigningSettingWrite) => Promise<unknown>;
  // ── Sending a request (CCM-R1-SIGN-WEB-B, the issuer's side) ────────────────────────────────
  /**
   * What sending one version for signature needs, before any request exists: its places, its pages'
   * scale, each way the filing is sent with whether Givmo can sign it, who the rule says signs (B4),
   * and why a send would be refused now. It never says a request is already open.
   */
  getSigningPlaces: (orgRef: string, documentId: string, versionId: string) => Promise<unknown>;
  /** Send one filing case's documents out for signature (a Compliance Owner or Global Admin). */
  issueSigningRequest: (orgRef: string, body: ComplianceSigningRequestIssue) => Promise<unknown>;
  /** A new version of an open request: its signers, titles, channel, zone and note. */
  reviseSigningRequest: (
    orgRef: string,
    requestId: string,
    body: ComplianceSigningRequestRevision,
  ) => Promise<unknown>;
  /** A later sign-by date; it only moves later. */
  extendSigningRequest: (
    orgRef: string,
    requestId: string,
    body: ComplianceSigningRequestExpiry,
  ) => Promise<unknown>;
  cancelSigningRequest: (
    orgRef: string,
    requestId: string,
    body: ComplianceSigningRequestCancellation,
  ) => Promise<unknown>;
  /** A completed request's signing record as a page shows it (B6): its lines; no download logged. */
  getSigningRecordLines: (orgRef: string, requestId: string) => Promise<unknown>;
  /** A short-lived link to a completed request's signing record, as a PDF or as data (logged). */
  getSigningEvidence: (
    orgRef: string,
    requestId: string,
    format: 'pdf' | 'json',
  ) => Promise<unknown>;
  /**
   * CCM-R1-SIGN-REDACT: remove one signer's sign-in email, IP address and browser from every
   * signing record of the organization, from a completed request's record (a sender's act).
   */
  removeSigningIdentity: (
    orgRef: string,
    requestId: string,
    body: { signer_user_id: string; basis: 'person_asked' | 'organization_decided' },
  ) => Promise<unknown>;
  /**
   * CCM-R1-SIGN-REDACT-UNFINISHED-REQUESTS: everyone the organization's signing requests hold
   * sign-in details for, newest first, a page at a time, each with the request to direct their
   * removal on (a sender's read, for the removal's own list).
   */
  listSigningPeople: (orgRef: string, page?: CompliancePage) => Promise<unknown>;
  /** A short-lived link to a completed request's export: the forms, the record, the digests. */
  getSigningExport: (orgRef: string, requestId: string) => Promise<unknown>;
  /** One durable cycle: the period a case files for, to name its filing (the signer page). */
  getRenewalCycle: (orgRef: string, cycleId: string) => Promise<unknown>;
  // ── Sending a filing (CCM-R1-SUBMIT-WEB) ──────────────────────────────────────────────────
  /**
   * A filing case's submission: whether Givmo can email it and, if not, why; the files, the email's
   * words, the agency's address, the fee and the signature they carry; the reply-contact choices
   * and who the email actually goes to; the case's latest authorization, every send, and what the
   * viewer can do now. Reading it sends nothing.
   */
  getSubmission: (orgRef: string, caseId: string) => Promise<unknown>;
  /**
   * Authorize sending the case's package exactly as reviewed (a Compliance Owner or Global Admin,
   * at the organization's sign-in check). Each echo is what the page showed.
   */
  authorizeSubmission: (
    orgRef: string,
    caseId: string,
    body: ComplianceSubmissionAuthorize,
  ) => Promise<unknown>;
  /** Revoke an active authorization, so nothing goes under it. */
  revokeSubmissionAuthorization: (orgRef: string, authorizationId: string) => Promise<unknown>;
  /** Send the authorized package once, as the send numbered `expected_attempt_ordinal`. */
  dispatchSubmission: (
    orgRef: string,
    authorizationId: string,
    body: ComplianceSubmissionDispatch,
  ) => Promise<unknown>;
  /** Record what a person found about a send whose outcome is uncertain; nothing is resent. */
  reconcileSubmissionAttempt: (
    orgRef: string,
    attemptId: string,
    body: ComplianceSubmissionReconciliation,
  ) => Promise<unknown>;
  /**
   * A short-lived link to the package as one zip (CCM-R1-SUBMIT-ZIP), named by the SHA-256 the
   * submission read served: the files as they go, or as an authorization bound them (logged).
   */
  getSubmissionPackage: (orgRef: string, caseId: string, packageSha256: string) => Promise<unknown>;
  /**
   * Record what the organization sent itself (CCM-R1-SUBMIT-HANDOFF): how and when, and the package
   * exactly as the read served it (by its SHA-256) or one of its own documents, with an optional proof
   * of sending. Givmo sends nothing; it answers the case's submission.
   */
  recordSubmissionHandoff: (
    orgRef: string,
    caseId: string,
    body: ComplianceSubmissionHandoffRecord,
  ) => Promise<unknown>;
  /**
   * Record what the agency told the organization about one of the case's sends (CCM-R1-RESULTS):
   * an acknowledgment, a rejection or a request for changes, its day, and the agency's own message
   * or the organization's own account. In the recorder's name; never acceptance. Answers the case's
   * submission.
   */
  recordSubmissionEvidence: (
    orgRef: string,
    caseId: string,
    body: ComplianceSubmissionEvidenceRecord,
  ) => Promise<unknown>;
  /**
   * Say a report or a record about a send is wrong (CCM-R1-RESULTS): the answer is kept beside it in
   * the answerer's name, and nothing counts the answered one any more. Answers the case's submission.
   */
  answerSubmissionEvidence: (
    orgRef: string,
    caseId: string,
    evidenceId: string,
    body: ComplianceSubmissionEvidenceAnswer,
  ) => Promise<unknown>;
  /**
   * Each filing case's latest send, or record of sending, with what is known about it so far
   * (CCM-R1-RESULTS), for the panel, the Calendar and Filings.
   */
  listSubmissionFollowThrough: (orgRef: string, page?: CompliancePage) => Promise<unknown>;
  // ── Givmo’s filing desk (CCM-R1-DESK-WEB) ─────────────────────────────────────────────────
  /** Hand the case to Givmo's filing desk ("Givmo handles it"); answers the case's submission. */
  handFilingToDesk: (orgRef: string, caseId: string) => Promise<unknown>;
  /** Take the case back from Givmo's desk; answers the case's submission. */
  takeFilingFromDesk: (orgRef: string, caseId: string) => Promise<unknown>;
  /** Tell Givmo's desk it can go on, once its need is met; answers the case's submission. */
  resumeDesk: (orgRef: string, caseId: string) => Promise<unknown>;
  /**
   * The organization's agreement for Givmo to file, if one is in force: when it was made, what it
   * covers, and whether the viewer may turn it off.
   */
  getDeskStanding: (orgRef: string) => Promise<unknown>;
  /** Turn the agreement off (a Compliance Owner or a Global Admin); answers the standing read. */
  revokeDeskStanding: (orgRef: string) => Promise<unknown>;
  /**
   * Record a copy of a version signed outside Givmo (CCM-R1-SIGN-OUTSIDE-B): the organization's
   * uploaded PDF and each person who signed it, as it states; a Compliance Owner's or a Global
   * Admin's. Answers the document with every version, the copy among them.
   */
  recordExecutedCopy: (
    orgRef: string,
    documentId: string,
    versionId: string,
    body: ExecutedCopyBody,
  ) => Promise<unknown>;
  // ── The review (CCM-R1-REVIEW-WEB; plan §1.8) ───────────────────────────────────────────────
  /** The open proposal and its newest revision (a typed 404 before the first). */
  getProposal: (orgRef: string) => Promise<unknown>;
  /**
   * Generate a revision for today: the newest one when nothing moved since it (nothing is written),
   * a new one otherwise. Someone who can make changes in Compliance; it adopts, binds and sends
   * nothing.
   */
  generateProposalRevision: (orgRef: string) => Promise<unknown>;
  getProposalRevision: (orgRef: string, revisionId: string) => Promise<unknown>;
  /** A revision's items, in the order they are presented, each with its latest decision. */
  listProposalItems: (
    orgRef: string,
    revisionId: string,
    page?: CompliancePage,
  ) => Promise<unknown>;
  /** A revision's results, every rule in scope, in the rules' order. */
  listProposalResults: (
    orgRef: string,
    revisionId: string,
    page?: CompliancePage,
  ) => Promise<unknown>;
  /** Record the organization's decisions on the revision it was shown, its digest echoed. */
  recordProposalReview: (
    orgRef: string,
    revisionId: string,
    body: ProposalReviewBody,
  ) => Promise<unknown>;
  /** The review recorded on a revision (a typed 404 before one exists). */
  getProposalReview: (orgRef: string, revisionId: string) => Promise<unknown>;
  /**
   * CCM-R1-REVIEW-ASSIGN: ask a teammate who can make changes in Compliance about an open item of
   * the revision shown. An open ask on the item is replaced (N43's Change); the same ask again
   * answers the open one, with no second email.
   */
  askAboutReviewItem: (orgRef: string, body: ReviewAskBody) => Promise<unknown>;
  /** CCM-R1-REVIEW-ASSIGN: withdraw an ask (N43's Withdraw). A closed ask answers as it stands. */
  withdrawReviewAsk: (orgRef: string, askId: string) => Promise<unknown>;
  /** CCM-R1-REVIEW-ASSIGN: the organization's asks, by who was asked and their state (frame 108). */
  listReviewAsks: (
    orgRef: string,
    filter: { askedUserId?: string; state?: 'open' | 'resolved' | 'withdrawn' | 'replaced' },
    page?: CompliancePage,
  ) => Promise<unknown>;
  /**
   * The signed storage PUT: `headers` are exactly the ones the signature binds (`uploadHeaders`),
   * sent as given.
   */
  putArtifactBytes: (
    uploadUrl: string,
    headers: Record<string, string>,
    file: Blob,
  ) => Promise<void>;
  resolveDisclosure: (registrySurface: string) => Promise<readonly string[] | null>;
}

/** CCM-R1-REVIEW-ASSIGN: an ask's request (`ComplianceProposalAskCreate`). */
export interface ReviewAskBody {
  revision_id: string;
  item_key: string;
  asked_user_id: string;
  note?: string;
}

/**
 * A review's request (`ComplianceProposalReviewCreate`): the revision's digest as it was shown, and
 * each decided item by its key. Typed here because the committed snapshot predates the route (the
 * adapter's escape hatch, `api-client.md` § "A whole ROUTE the snapshot lacks").
 */
export interface ProposalReviewBody {
  revision_sha256: string;
  decisions: { item_key: string; decision: 'accept' | 'deny' | 'defer'; reason?: string }[];
}

/**
 * The host's answer to "may this person open this module" (D-CPO-24 standing), three-valued:
 * `'available'` is a REPORTED, held, switched-on standing; `'unavailable'` is a reported answer
 * that is anything else (not held, off, setting up, not offered); `'unknown'` is no answer yet.
 */
export type ComplianceModuleStanding = 'available' | 'unavailable' | 'unknown';

/**
 * A host's door to a place on its own pages: a real link — `href` for the anchor, so open-in-new-tab
 * keeps working — and `open` for the in-app hop. The package never spells a host route itself.
 */
export interface ComplianceHostDoor {
  href: string;
  open: () => void;
}

/**
 * The viewer's own "Email me Compliance reminders" switch, as the host answers for it (D-CCM-18;
 * CCM-R1-REMIND-WEB).
 */
export interface ComplianceRemindersHost {
  /**
   * Whether the viewer's OWN preferences read serves `compliance.reminders.email`: `true` / `false`
   * once it has answered (a failed read answers `false`), `undefined` until then. The key is served
   * to exactly the people reminders go to — Compliance Owners and Global Admins — so this is the
   * Owner-or-Member question, answered without a read of its own.
   */
  switchServed: boolean | undefined;
  /** Where that switch lives: the host's Settings › Your settings › Notifications. */
  settings: ComplianceHostDoor;
}

export interface ComplianceHostState {
  orgRef: string | null;
  isReady: boolean;
  isPending: boolean;
  isError: boolean;
  error: unknown;
  /**
   * The host's standing answer, and it is LOAD-BEARING (CCM-MOD1B): the module's server operations
   * fire only on `'available'`. Anything else schedules no read, renders no data region, and refuses
   * every operation at the boundary (`compliance-standing.ts`). The host's gate renders the state;
   * this package renders none of it.
   */
  moduleStanding: ComplianceModuleStanding;
  /**
   * The viewer's own reminders switch, for the no-recipients notice. Absent on a host with no
   * Settings page: the notice then speaks to everyone in the Member's words.
   */
  reminders?: ComplianceRemindersHost;
  /**
   * Whether the viewer may change the organization's Compliance records (the module's write
   * string, as the host's standing read reports it). Filings offers its organizing changes — move a
   * filing, start, rename, reorder or remove a group — only on `true` (DESIGN-NOTE-1b §2.4: a viewer
   * sees the groups, "Open" and "Copy link"). The server gates every write whatever this says;
   * absent is no.
   */
  canWrite?: boolean;
  /**
   * An opaque key for the signed-in person, so what this browser remembers per person (the groups
   * they collapsed on Filings) is theirs. Absent: nothing is remembered between visits.
   */
  viewerKey?: string;
  /**
   * CCM-R1-REVIEW-ASSIGN: the signed-in person's own user id, the backend's (never the Firebase
   * uid), so the review knows what was asked of them (frames 94b and 108). Absent: nothing reads as
   * asked of the viewer.
   */
  viewerUserId?: string;
  /**
   * CCM-R1-REVIEW-ASSIGN: the people who can make changes in Compliance (N42's access line), the
   * ones "Ask a teammate" may ask, named as the send sheet names its signers. `undefined` while the
   * roster reads.
   */
  complianceWriters?: readonly { userId: string; name: string }[];
  /**
   * A person's name by their user id, from the host's own roster, for "Added by …" on a filing's
   * years. `undefined` for anyone the host cannot name: the line then says "Added on …" (copy
   * ruling item 8). Absent: no names.
   */
  personName?: (userId: string) => string | undefined;
  /**
   * Everyone on the organization's roster, each named as its Team page names them (their name, else
   * their email), for the removal of sign-in details (CCM-R1-SIGN-REDACT-EARLIER-SIGNERS,
   * CCM-R1-SIGN-REDACT-UNFINISHED-REQUESTS): only someone it doesn't list is "no longer in your
   * organization" (cut 1008-105's review, F2). `undefined` while the roster reads, `null` when it
   * couldn't be read: the removal names nobody until it has the roster.
   */
  organizationMembers?: readonly { userId: string; name: string }[] | null;
  /**
   * The organization's name as the host shows it, for the lines that name it ("as {Org} states it",
   * the sign-in check prompt's "{Org} asks everyone…"). Absent: those lines say "your organization".
   */
  organizationName?: string;
  /**
   * The host's offer to set up a passkey, opened from Sign — or from Authorize and send
   * (CCM-R1-SIGN-PASSKEY), or from the sign-in check card's Save (`level`; Account Security's
   * AS-PWL-2e) — when the organization's sign-in check can't be met with anything this account
   * holds (S10; D-AS-24/25/26). `act` says which, so the offer's "Not now" line names it. It settles
   * once: `created`, `declined`, or `unavailable`; after `created` Sign runs again, while Authorize
   * and send and Save wait for a press. Absent: no offer, the line alone. The charity console's is
   * Account Security's card (`@givmo/passkeys`), its from-Sign, from-Send or from-level variant.
   */
  renderPasskeySetupOffer?: (props: {
    act: 'sign' | 'send' | 'level';
    onSettled: (outcome: PasskeySetupOutcome) => void;
  }) => ReactNode;
  /**
   * Whether the viewer may send documents for signature and change the sign-in check
   * (`compliance:send_for_signature` in the host's standing read; a Compliance Owner or Global
   * Admin). `undefined` when nothing is reported: Send then shows as for a sender and the server
   * decides, and the Owner line ("Only a Compliance Owner or a Global Admin…") never shows on a
   * guess (CCM-R1-SIGN-WEB-B).
   */
  canSendForSignature?: boolean;
  /**
   * The people who can sign, from the host's own roster: everyone holding Compliance, with the name
   * and email the send sheet's Signer list shows. `undefined` while the roster reads.
   */
  complianceSigners?: readonly ComplianceSignerCandidate[];
  /**
   * Where "Invite someone to Compliance" leads (the module's own Team page), for a viewer who may
   * invite. Absent: the send sheet shows its access sentence alone.
   */
  inviteToCompliance?: ComplianceHostDoor;
  /**
   * Where frame 21's "Go to settings" leads (CCM-R1-DESK-WEB): the host's Settings, Compliance's
   * section first, where "Use AI on your organization’s data" is turned on. Absent: the dialog
   * offers "Got it" alone.
   */
  complianceSettings?: ComplianceHostDoor;
  /**
   * The people who can authorize sending a filing (`compliance:send_for_signature`: a Compliance
   * Owner or a Global Admin), from the host's own roster, for a Member's "At {org}, that's …"
   * (slice 3's frame 71). `undefined` while the roster reads, or where the host can't tell.
   */
  complianceAuthorizers?: readonly { userId: string; name: string }[];
  /**
   * Whether the viewer has an AI connected to this organization that may write its Compliance
   * records — a connected-AI grant acting as the viewer whose consent-time scopes include
   * `org.compliance.write` — from the host's own read (CCM-R1-SUBMIT-MCP). Only on `true` does the
   * send review offer "Let your AI send this" (UX slice 3's P6: never keyed on Givmo's AI setting).
   * `undefined` while that reads, or where the host can't tell: the offer never shows on a guess,
   * and the server decides who may send in any case.
   */
  connectedAiCanSend?: boolean;
}

/** One person the send sheet can name to sign: holds Compliance, named by the host's roster. */
export interface ComplianceSignerCandidate {
  userId: string;
  name: string;
  email?: string;
}

/** How a host's passkey setup offer ended (`renderPasskeySetupOffer`). */
export type PasskeySetupOutcome = 'created' | 'declined' | 'unavailable';

interface ComplianceContextValue extends ComplianceHostState {
  /** The injected operations — guarded to refuse while `moduleStanding` is not `'available'`. */
  operations: ComplianceOperations;
}

const ComplianceContext = createContext<ComplianceContextValue | null>(null);

export function ComplianceProvider({
  host,
  operations,
  children,
}: {
  host: ComplianceHostState;
  operations: ComplianceOperations;
  children: ReactNode;
}) {
  // The guard is applied HERE, once, so no page, panel or mutation can reach an unguarded adapter
  // through this context — and a live revocation swaps every operation in the same render the
  // standing changes.
  const guarded = useMemo(
    () => guardComplianceOperations(operations, host.moduleStanding),
    [operations, host.moduleStanding],
  );
  return (
    <ComplianceContext.Provider value={{ ...host, operations: guarded }}>
      {children}
    </ComplianceContext.Provider>
  );
}

export function useCompliance(): ComplianceContextValue {
  const value = useContext(ComplianceContext);
  if (value === null) {
    throw new Error('@givmo/compliance: ComplianceProvider is missing');
  }
  return value;
}
